/**
 * Mapa de componentes lazy-loaded por ID.
 * 
 * Cada chave corresponde ao campo `componente` do menu (config/menu.ts).
 * Os imports são relativos a frontend/src/components/
 */
import { lazy } from 'react';

// ─── Tipagem ────────────────────────────────────────────
export type RouteComponentMap = Record<string, React.LazyExoticComponent<React.ComponentType<any>>>;

// ─── INTEGRAÇÃO (ESCOLAR) ───────────────────────────────
const EscolaDashboard = lazy(() => import('../components/EscolaDashboard'));
const EscolaDados = lazy(() => import('../components/EscolaDados'));
const CadastroManualAlunos = lazy(() => import('../components/CadastroManualAlunos'));

// ─── CORPORATIVO — MONITOR ──────────────────────────────
const Monitor = lazy(() => import('../components/corporativo/monitor/Monitor'));
const MonitorV2 = lazy(() => import('../components/corporativo/monitor-v2/MonitorV2'));
const CentralAjudaAdmin = lazy(() => import('../components/corporativo/ajuda/CentralAjudaAdmin'));

// ─── CORPORATIVO — ATENDIMENTO (spec 0108) ──────────────
const FilaFeedback = lazy(() => import('../components/atendimento/FilaFeedback'));
const PlanilhasDasEscolas = lazy(() => import('../components/corporativo/monitor/planilhas-escolas/PlanilhasDasEscolas'));
const DecidirPendencias = lazy(() => import('../components/corporativo/monitor/planilhas-escolas/DecidirPendencias'));

// ─── CORPORATIVO — CADASTROS ────────────────────────────
const CadastrosAlunos = lazy(() => import('../components/corporativo/cadastros/CadastrosAlunos'));
const CadastrosEscolas = lazy(() => import('../components/corporativo/cadastros/CadastrosEscolas'));
const CadastrosTransportador = lazy(() => import('../components/corporativo/cadastros/CadastrosTransportador'));
const CadastrosPessoas = lazy(() => import('../components/corporativo/cadastros/CadastrosPessoas'));
const CadastrosVeiculos = lazy(() => import('../components/corporativo/cadastros/CadastrosVeiculos'));

// ─── CORPORATIVO — PROCESSOS ────────────────────────────
const GestaoFechamento = lazy(() => import('../components/corporativo/processos/GestaoFechamento'));
const GestaoPagamentos = lazy(() => import('../components/corporativo/processos/GestaoPagamentos'));
const FechamentoCooperado = lazy(() => import('../components/corporativo/processos/fechamento-cooperado/FechamentoCooperado'));
const ParametrosFechamento = lazy(() => import('../components/corporativo/processos/parametros-fechamento/ParametrosFechamento'));
const Notificacoes = lazy(() => import('../components/corporativo/processos/Notificacoes'));

// ─── CORPORATIVO — RELATÓRIOS ───────────────────────────
const DemonstrativoFinanceiro = lazy(() => import('../components/corporativo/relatorios/DemonstrativoFinanceiro'));
const DemonstrativoAnalitico = lazy(() => import('../components/corporativo/relatorios/DemonstrativoAnalitico'));
const DemonstrativoSintetico = lazy(() => import('../components/corporativo/relatorios/DemonstrativoSintetico'));
const ListaAlunosEscolas = lazy(() => import('../components/corporativo/relatorios/ListaAlunosEscolas'));
const ListaAlunosTransportadores = lazy(() => import('../components/corporativo/relatorios/ListaAlunosTransportadores'));

// ─── FRETAMENTO — CT-e ──────────────────────────────────
const MonitorFretamento = lazy(() => import('../components/fretamento/monitor/MonitorFretamento'));
const EmissaoCTeOS = lazy(() => import('../components/fretamento/emissao/EmissaoCTeOS'));
const ConsultaCTeOS = lazy(() => import('../components/fretamento/consulta/ConsultaCTeOS'));
const RelatoriosFretamento = lazy(() => import('../components/fretamento/relatorios/RelatoriosFretamento'));
const FiscalDashboard = lazy(() => import('../components/fretamento/fiscal/FiscalDashboard'));

// ─── FRETAMENTO — ATF ───────────────────────────────────
const MonitorAtf = lazy(() => import('../components/fretamento/atf/MonitorAtf'));
const EmissaoAtf = lazy(() => import('../components/fretamento/atf/AtfFormulario'));
const PlanosTarifaAtf = lazy(() => import('../components/fretamento/atf/PlanosTarifaAtf'));

// ─── FRETAMENTO — Configurações ─────────────────────────
// ParametrosEmissao substitui ParametrosAtf + ConfiguracoesFretamento (a tela
// única, com abas por tipo de emissão).
const ParametrosEmissao = lazy(() => import('../components/fretamento/parametros/ParametrosEmissao'));
const CreditosDashboard = lazy(() => import('../components/fretamento/creditos/CreditosDashboard'));

// ─── FRETAMENTO — Pendências de Cadastro (spec 0028) ────
// Não estava aqui: a barra lateral não tinha a tela, e o menu do topo a
// carregava por conta própria. Desde a 0087 o menu inteiro passa por este mapa.
const PendenciasCadastro = lazy(() => import('../components/fretamento/cadastro/PendenciasCadastro'));

// ─── ROTEIRIZAÇÃO (spec 0098) ───────────────────────────
// Uma tela por aba, todas no mesmo pedaço do bundle (o módulo `telas`).
const telaDaRoteirizacao = (nome: keyof typeof import('../components/roteirizacao/telas')) =>
  lazy(() => import('../components/roteirizacao/telas').then((m) => ({ default: m[nome] })));
const RoteirizacaoVisaoGeral = telaDaRoteirizacao('VisaoGeral');
const RoteirizacaoViagens = telaDaRoteirizacao('Viagens');
const RoteirizacaoAtendimento = telaDaRoteirizacao('Atendimento');
const RoteirizacaoOcorrencias = telaDaRoteirizacao('Ocorrencias');
const RoteirizacaoRotas = telaDaRoteirizacao('Rotas');
const RoteirizacaoCalendario = telaDaRoteirizacao('Calendario');
const RoteirizacaoProntidao = telaDaRoteirizacao('Prontidao');
const RoteirizacaoCoordenadas = telaDaRoteirizacao('Coordenadas');
const RoteirizacaoPedidos = telaDaRoteirizacao('Pedidos');
const RoteirizacaoGuarda = telaDaRoteirizacao('Guarda');
const RoteirizacaoImportarResponsaveis = telaDaRoteirizacao('ImportarResponsaveis');
const RoteirizacaoParametros = telaDaRoteirizacao('Parametros');
const RoteirizacaoWhatsapp = telaDaRoteirizacao('Whatsapp');
const RoteirizacaoPrivacidade = telaDaRoteirizacao('Privacidade');

// ─── VIVAN ──────────────────────────────────────────────
const VivanDashboard = lazy(() => import('../components/corporativo/vivan/VivanDashboard'));
const PassageirosLista = lazy(() => import('../components/corporativo/vivan/passageiros/PassageirosLista'));
const ContratosLista = lazy(() => import('../components/corporativo/vivan/contratos/ContratosLista'));
const MensalidadesLista = lazy(() => import('../components/corporativo/vivan/financeiro/MensalidadesLista'));

// ─── MAPA EXPORTADO ─────────────────────────────────────
export const ROUTE_COMPONENTS: RouteComponentMap = {
  // Integração
  EscolaDashboard,
  EscolaDados,
  CadastroManualAlunos,

  // Corporativo - Monitor
  Monitor,
  MonitorV2,
  CentralAjudaAdmin,
  FilaFeedback,
  PlanilhasDasEscolas,
  DecidirPendencias,

  // Corporativo - Cadastros
  CadastrosAlunos,
  CadastrosEscolas,
  CadastrosTransportador,
  CadastrosPessoas,
  CadastrosVeiculos,

  // Corporativo - Processos
  GestaoFechamento,
  GestaoPagamentos,
  FechamentoCooperado,
  ParametrosFechamento,
  Notificacoes,

  // Corporativo - Relatórios
  DemonstrativoFinanceiro,
  DemonstrativoAnalitico,
  DemonstrativoSintetico,
  ListaAlunosEscolas,
  ListaAlunosTransportadores,

  // Fretamento - CT-e
  MonitorFretamento,
  EmissaoCTeOS,
  ConsultaCTeOS,
  RelatoriosFretamento,
  FiscalDashboard,

  // Fretamento - ATF
  MonitorAtf,
  EmissaoAtf,
  PlanosTarifaAtf,

  // Fretamento - Configurações
  ParametrosEmissao,
  CreditosDashboard,
  PendenciasCadastro,

  // Roteirização
  RoteirizacaoVisaoGeral,
  RoteirizacaoViagens,
  RoteirizacaoAtendimento,
  RoteirizacaoOcorrencias,
  RoteirizacaoRotas,
  RoteirizacaoCalendario,
  RoteirizacaoProntidao,
  RoteirizacaoCoordenadas,
  RoteirizacaoPedidos,
  RoteirizacaoGuarda,
  RoteirizacaoImportarResponsaveis,
  RoteirizacaoParametros,
  RoteirizacaoWhatsapp,
  RoteirizacaoPrivacidade,

  // ViVan
  VivanDashboard,
  PassageirosLista,
  ContratosLista,
  MensalidadesLista,
};
